import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@flowcore-water/ui';
import type { ThemeColors } from '@flowcore-water/ui';
import { monitoring } from '../services/monitoringService';
import type { MonitoringEvent, MonitoringEventType, PerformanceMetrics } from '../services/monitoringService';

type Tab = 'errors' | 'performance' | 'actions' | 'all';

const TAB_LABELS: Record<Tab, string> = {
  errors: 'Errors',
  performance: 'Performance',
  actions: 'Activity',
  all: 'All Events',
};

const getSeverityColor = (severity: string, t: ThemeColors): string =>
  ({
    critical: t.fail,
    error: t.fail,
    warning: t.warn,
    info: t.info,
  } as Record<string, string>)[severity] ?? t.textMuted;

const getTypeColor = (type: string, t: ThemeColors): string =>
  ({
    error: t.fail,
    performance: t.success,
    action: t.info,
    navigation: t.sectionHeading,
  } as Record<string, string>)[type] ?? t.textMuted;

const fmt = (ts: number) => {
  const d = new Date(ts);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const fmtMs = (v?: number) => v != null ? `${Math.round(v)}ms` : '—';

export const MonitoringDashboard: React.FC = () => {
  const { t } = useTheme();
  const [tab, setTab] = useState<Tab>('errors');
  const [events, setEvents] = useState<MonitoringEvent[]>([]);
  const [metrics, setMetrics] = useState<PerformanceMetrics[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [counts, setCounts] = useState<Record<MonitoringEventType, number>>({ error: 0, performance: 0, action: 0, navigation: 0 });

  const refresh = useCallback(() => {
    const typeFilter = tab === 'errors' ? 'error' : tab === 'actions' ? 'action' : tab === 'performance' ? undefined : undefined;
    if (tab === 'performance') {
      setMetrics(monitoring.getMetrics(50));
      setEvents([]);
    } else {
      setEvents(monitoring.getEvents({ type: typeFilter as MonitoringEventType | undefined, limit: 200 }));
      setMetrics([]);
    }
    setCounts(monitoring.getEventCounts());
  }, [tab]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    const interval = setInterval(refresh, 10_000);
    return () => clearInterval(interval);
  }, [refresh]);

  const cardStyle: React.CSSProperties = {
    background: t.cardBg,
    border: `1px solid ${t.border}`,
    borderRadius: '0.75rem',
  };

  const latestMetrics = metrics[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: t.textPrimary }}>Monitoring</h1>
          <p className="mt-1 text-sm" style={{ color: t.textSecondary }}>
            Error tracking, performance metrics, and activity log
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={refresh}
            className="rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors"
            style={{ background: t.buttonBg, borderColor: t.buttonBorder, color: t.buttonText }}
          >
            Refresh
          </button>
          <button
            onClick={() => { monitoring.clearAll(); refresh(); }}
            className="rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors"
            style={{ background: t.failBg, borderColor: t.failBorder, color: t.fail }}
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {([
          { label: 'Errors', count: counts.error, color: t.fail, icon: '!' },
          { label: 'Actions', count: counts.action, color: t.info, icon: '→' },
          { label: 'Navigations', count: counts.navigation, color: t.sectionHeading, icon: '↗' },
          { label: 'FCP', count: null, value: fmtMs(latestMetrics?.fcp), color: t.success, icon: '⚡' },
        ]).map((card) => (
          <div key={card.label} style={cardStyle} className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <span
                className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold"
                style={{ background: `${card.color}20`, color: card.color }}
              >
                {card.icon}
              </span>
              <span className="text-xs font-medium uppercase tracking-wide" style={{ color: t.textMuted }}>
                {card.label}
              </span>
            </div>
            <p className="text-2xl font-bold font-mono" style={{ color: card.color }}>
              {card.count != null ? card.count : card.value}
            </p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg p-1" style={{ background: 'rgba(0,0,0,0.25)' }}>
        {(Object.keys(TAB_LABELS) as Tab[]).map((key) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className="px-4 py-2 rounded-md text-sm font-medium transition-all"
            style={{
              background: tab === key ? `${t.accent}18` : 'transparent',
              color: tab === key ? t.accent : t.textSecondary,
              border: tab === key ? `1px solid ${t.accent}50` : '1px solid transparent',
            }}
          >
            {TAB_LABELS[key]}
            {key === 'errors' && counts.error > 0 && (
              <span className="ml-2 px-1.5 py-0.5 rounded-full text-xs" style={{ background: t.failBg, color: t.fail }}>
                {counts.error}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Performance Tab */}
      {tab === 'performance' && (
        <div className="space-y-4">
          {/* Core Web Vitals */}
          {latestMetrics && (
            <div style={cardStyle} className="p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wide mb-4" style={{ color: t.textSecondary }}>
                Core Web Vitals (latest)
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {([
                  { label: 'FCP', value: latestMetrics.fcp, good: 1800, poor: 3000, noMs: false },
                  { label: 'LCP', value: latestMetrics.lcp, good: 2500, poor: 4000, noMs: false },
                  { label: 'CLS', value: latestMetrics.cls, good: 0.1, poor: 0.25, noMs: true },
                  { label: 'TTFB', value: latestMetrics.ttfb, good: 800, poor: 1800, noMs: false },
                  { label: 'Page Load', value: latestMetrics.pageLoad, good: 3000, poor: 6000, noMs: false },
                ]).map((metric) => {
                  const val = metric.value;
                  const color = val == null ? t.textMuted
                    : val <= metric.good ? t.success
                    : val <= metric.poor ? t.warn
                    : t.fail;
                  return (
                    <div key={metric.label}>
                      <p className="text-xs font-medium uppercase tracking-wide mb-1" style={{ color: t.textMuted }}>
                        {metric.label}
                      </p>
                      <p className="text-xl font-bold font-mono" style={{ color }}>
                        {val != null ? (metric.noMs ? val.toFixed(3) : `${Math.round(val)}ms`) : '—'}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Metrics history */}
          <div className="divide-y" style={{ ...cardStyle, borderColor: t.border }}>
            <div className="px-5 py-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: t.textSecondary }}>
                Recent Page Loads
              </h3>
            </div>
            {metrics.length === 0 && (
              <div className="px-5 py-8 text-center text-sm" style={{ color: t.textMuted }}>
                No performance data yet. Navigate around to generate metrics.
              </div>
            )}
            {metrics.map((m, i) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between gap-4 text-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-mono text-xs shrink-0" style={{ color: t.textMuted }}>{fmt(m.timestamp)}</span>
                  <span className="truncate" style={{ color: t.textPrimary }}>{m.url}</span>
                </div>
                <div className="flex items-center gap-4 shrink-0 font-mono text-xs">
                  <span style={{ color: t.textSecondary }}>FCP {fmtMs(m.fcp)}</span>
                  <span style={{ color: t.textSecondary }}>LCP {fmtMs(m.lcp)}</span>
                  <span style={{ color: t.textSecondary }}>Load {fmtMs(m.pageLoad)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Events Tab (errors, actions, all) */}
      {tab !== 'performance' && (
        <div className="divide-y" style={{ ...cardStyle, borderColor: t.border }}>
          {events.length === 0 && (
            <div className="px-5 py-8 text-center text-sm" style={{ color: t.textMuted }}>
              No events recorded yet.
            </div>
          )}
          {events.map((event) => (
            <div key={event.id}>
              {/* Event row */}
              <button
                className="w-full text-left px-5 py-3 flex items-center gap-3 transition-colors"
                style={{ background: expandedId === event.id ? t.surfaceHover : 'transparent' }}
                onClick={() => setExpandedId(expandedId === event.id ? null : event.id)}
              >
                {/* Severity dot */}
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ background: getSeverityColor(event.severity, t) }}
                />
                {/* Type badge */}
                <span
                  className="text-xs font-mono px-2 py-0.5 rounded shrink-0"
                  style={{
                    background: `${getTypeColor(event.type, t)}15`,
                    color: getTypeColor(event.type, t),
                  }}
                >
                  {event.type}
                </span>
                {/* Message */}
                <span className="truncate text-sm" style={{ color: t.textPrimary }}>
                  {event.message}
                </span>
                {/* Timestamp */}
                <span className="ml-auto text-xs font-mono shrink-0" style={{ color: t.textMuted }}>
                  {fmt(event.timestamp)}
                </span>
              </button>

              {/* Expanded detail */}
              {expandedId === event.id && (
                <div className="px-5 pb-4 space-y-3" style={{ background: t.surfaceHover }}>
                  {/* User context */}
                  {event.user && (
                    <div className="text-xs" style={{ color: t.textSecondary }}>
                      <span className="font-semibold">User:</span> {event.user.email} ({event.user.role})
                    </div>
                  )}
                  {/* URL */}
                  {event.url && (
                    <div className="text-xs" style={{ color: t.textSecondary }}>
                      <span className="font-semibold">URL:</span> {event.url}
                    </div>
                  )}
                  {/* Meta */}
                  {event.meta && Object.keys(event.meta).length > 0 && (
                    <div>
                      <p className="text-xs font-semibold mb-1" style={{ color: t.textSecondary }}>Metadata</p>
                      <pre
                        className="text-xs p-3 rounded-lg overflow-x-auto font-mono"
                        style={{ background: t.pageBg, color: t.textSecondary }}
                      >
                        {JSON.stringify(event.meta, null, 2)}
                      </pre>
                    </div>
                  )}
                  {/* Stack trace */}
                  {event.stack && (
                    <div>
                      <p className="text-xs font-semibold mb-1" style={{ color: t.textSecondary }}>Stack Trace</p>
                      <pre
                        className="text-xs p-3 rounded-lg overflow-x-auto font-mono leading-relaxed"
                        style={{ background: t.pageBg, color: t.fail }}
                      >
                        {event.stack}
                      </pre>
                    </div>
                  )}
                  {/* Breadcrumbs */}
                  {event.breadcrumbs && event.breadcrumbs.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold mb-1" style={{ color: t.textSecondary }}>
                        Breadcrumbs ({event.breadcrumbs.length})
                      </p>
                      <div className="space-y-1">
                        {event.breadcrumbs.map((bc, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <span className="font-mono shrink-0" style={{ color: t.textMuted }}>
                              {new Date(bc.timestamp).toLocaleTimeString()}
                            </span>
                            <span
                              className="px-1.5 py-0.5 rounded text-xs shrink-0"
                              style={{
                                background: `${getTypeColor(bc.type, t)}15`,
                                color: getTypeColor(bc.type, t),
                              }}
                            >
                              {bc.type}
                            </span>
                            <span style={{ color: t.textSecondary }}>{bc.message}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
